import { useQuery } from "@apollo/client";
import { ISelfieQuery } from "~/lib/queries/__generated__/SelfieQuery.generated";
import { findCountryNameByCode } from "~/src/utils/countries";
import AvailableStatesWithCountriesQuery from "~/src/shared/graphql/AvailableStatesWithCountriesQuery";
import { IAvailableStatesWithCountriesQuery } from "~/src/shared/graphql/__generated__/AvailableStatesWithCountriesQuery.generated";
import { useIntl, IntlShape } from "react-intl";

type TCountryStateFilterInput = {
  country: string;
  state: string;
  selectDefaultState?: boolean;
};

type TCountryStateFilterOutput = {
  loading: boolean;
  countryOptions: TFilterSelectOption[];
  stateOptions: (TFilterSelectOption | TFilterSelectSection)[];
  defaultSelectedCountry: TFilterSelectOption | null;
  defaultSelectedState: TFilterSelectOption | null;
  cantFilterByState: boolean;
  placeholders: {
    country: string;
    state: string;
  };
};

export default function useCountryStateFilters(input: TCountryStateFilterInput): TCountryStateFilterOutput {
  const intl = useIntl();
  const { data, loading } = useQuery<IAvailableStatesWithCountriesQuery>(AvailableStatesWithCountriesQuery);

  const placeholders = {
    country: intl.formatMessage({ defaultMessage: 'Filter by country', id: '5YeTlf' }),
    state: getStateFilterPlaceholder(intl, input.country),
  };

  if (loading) {
    return {
      loading: true,
      countryOptions: [],
      stateOptions: [],
      defaultSelectedCountry: null,
      defaultSelectedState: null,
      cantFilterByState: false,
      placeholders,
    };
  }

  const availableStatesWithCountries = data?.availableStatesWithCountries || [];
  const countryOptions = getCountryOptions(availableStatesWithCountries);
  const stateOptions = getStateOptionsForCountry(availableStatesWithCountries, input.country);

  const defaultFilterOption = (
    filterOptionValue: string,
    availableOptions: (TFilterSelectOption | TFilterSelectSection)[],
    selectDefaultState?: boolean,
  ) => {
    if (!filterOptionValue) {
      if (!selectDefaultState) {
        return null;
      }

      if (isSectionedOptions(availableOptions)) {
        return {
          label: availableOptions[0].options[0].label,
          value: availableOptions[0].options[0].value,
        };
      } else if (isFlatOptions(availableOptions)) {
        return {
          label: availableOptions[0].label,
          value: availableOptions[0].value,
        };
      }

      return null;
    }

    let allOptions: TFilterSelectOption[] = [];

    if (isSectionedOptions(availableOptions)) {
      allOptions = allOptions.concat(...availableOptions.map(({ options }) => options));
    } else if (isFlatOptions(availableOptions)) {
      allOptions = availableOptions;
    }

    return (
      allOptions.find(candidate => {
        return candidate.value === filterOptionValue;
      }) || null
    );
  };

  return {
    loading: false,
    countryOptions,
    stateOptions,
    defaultSelectedCountry: defaultFilterOption(input.country, countryOptions),
    defaultSelectedState: defaultFilterOption(input.state, stateOptions, input.selectDefaultState),
    cantFilterByState: ![
      "US",
      "CA",
      "ZA",
      "AU",
      "IE",
      "BR",
      "DE",
      "MX",
      "AR",
      "IT",
      "NL",
      "DK",
      "NZ",
      "GB",
      "SE",
      "FI",
    ].includes(input.country),
    placeholders,
  };
}

type TFilterSelectOption = {
  label: string;
  value: string;
};

type TFilterSelectSection = {
  section: string;
  options: TFilterSelectOption[];
};

type TFilterSelectOptions = (TFilterSelectOption | TFilterSelectSection)[];

function isSectionedOptions(value: TFilterSelectOptions): value is TFilterSelectSection[] {
  return Array.isArray(value) && value.length > 0 && (value[0] as any).section;
}

function isFlatOptions(value: TFilterSelectOptions): value is TFilterSelectOption[] {
  return Array.isArray(value) && value.length > 0 && (value[0] as any).value;
}

function getCountryOptions(
  statesWithCountriesData: IAvailableStatesWithCountriesQuery["availableStatesWithCountries"],
) {
  const countries = statesWithCountriesData.map(stateWithCountry => {
    return stateWithCountry.country;
  });

  return Array.from(new Set(countries))
    .map(countryCode => {
      return {
        label: findCountryNameByCode(countryCode) || "",
        value: countryCode,
      };
    })
    .sort(sortAlphabetically);
}

function getStateOptionsForCountry<Data extends IAvailableStatesWithCountriesQuery["availableStatesWithCountries"]>(
  statesWithCountriesData: Data,
  forCountry: string,
) {
  const makeFilterOption = ({ friendlyStateName, state }: Data[0]) => ({
    label: friendlyStateName,
    value: state,
  });

  if (forCountry !== "US") {
    return statesWithCountriesData
      .filter(({ country }) => country === forCountry)
      .map(makeFilterOption)
      .sort(sortAlphabetically);
  }

  const unitedStates = statesWithCountriesData.filter(({ country }) => country === "US");
  const territories = ["GU", "PR", "YT", "NT", "NU"];

  return [
    {
      section: "State",
      options: unitedStates
        .filter(({ state }) => !territories.includes(state))
        .map(makeFilterOption)
        .sort(sortAlphabetically),
    },
    {
      section: "Territories",
      options: unitedStates
        .filter(({ state }) => territories.includes(state))
        .map(makeFilterOption)
        .sort(sortAlphabetically),
    },
  ];
}

function getStateFilterPlaceholder(intl: IntlShape, country: string) {
  if (country === "US") {
    return intl.formatMessage({ defaultMessage: 'Filter by State/Territory...', id: 'REIo7m' });
  }

  if (country === "CA" || country === "ZA") {
    return intl.formatMessage({ defaultMessage: 'Filter by Province...', id: 'hH7+0+' });
  }

  if (country === "AU") {
    return intl.formatMessage({ defaultMessage: 'Filter by State...', id: 'qGg0Vb' });
  }

  if (country === "IE") {
    return intl.formatMessage({ defaultMessage: 'Filter by Region...', id: 'HvuZrV' });
  }

  return intl.formatMessage({ defaultMessage: 'Filter by State/Territory/Province...', id: 'wIceQ1' });
}

function sortAlphabetically(a: TFilterSelectOption, b: TFilterSelectOption) {
  return a.label.localeCompare(b.label);
}

export function getUserCountry(user: ISelfieQuery["selfie"]) {
  if (!user) {
    return "";
  }

  if (
    user.signInAttempts &&
    user.signInAttempts[0] &&
    user.signInAttempts[0].lookup &&
    user.signInAttempts[0].lookup.country_code
  ) {
    return user.signInAttempts[0].lookup.country_code;
  }

  return "";
}
